import { MessageCircle, Users, Check, X } from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub, Pill } from "@/components/web/atoms";
import { Reveal } from "@/components/web/home/primitives";

/* Section 15 — differentiation, without naming competitors or making
   unsupported comparative claims. */
const COLS = [
  {
    key: "ai-chat", icon: MessageCircle, title: "Typical AI chat", muted: true,
    rows: [
      { t: "Ask a question", ok: true },
      { t: "Get an answer", ok: true },
      { t: "Conversation disappears into history", ok: false },
      { t: "No people, no follow-through", ok: false },
    ],
  },
  {
    key: "team-chat", icon: Users, title: "Typical team chat", muted: true,
    rows: [
      { t: "Talk to people", ok: true },
      { t: "Search old messages", ok: true },
      { t: "AI is a bolt-on add-on", ok: false },
      { t: "Answers never become work", ok: false },
    ],
  },
  {
    key: "teamnest", title: "TeamNest", highlight: true,
    rows: [
      { t: "Research with multiple AI models", ok: true },
      { t: "Collaborate with people", ok: true },
      { t: "Turn findings into actions", ok: true },
      { t: "Automate work", ok: true },
      { t: "Preserve knowledge", ok: true },
    ],
  },
];

export default function DifferentiationSection() {
  return (
    <section
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] scroll-mt-20"
      data-testid="home-differentiation"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="brand" className="mb-4">Why TeamNest is different</Eyebrow>
          <SectionTitle>Traditional chat saves conversations. Traditional AI answers questions.</SectionTitle>
          <SectionSub className="mt-5">
            TeamNest connects human conversation, AI research, actions, automation and persistent knowledge
            into one continuous workspace.
          </SectionSub>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          {COLS.map((c, i) => (
            <Reveal key={c.key} delay={i * 80}>
              <div
                className={`h-full rounded-[18px] p-6 border ${
                  c.highlight
                    ? "border-[var(--w-brand)]/40 bg-[var(--w-brand-tint)]"
                    : "border-[var(--w-hairline)] bg-[var(--w-surface)]"
                }`}
                data-testid={`diff-${c.key}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  {c.icon && (
                    <span className="w-8 h-8 rounded-lg bg-[var(--w-surface2)] flex items-center justify-center">
                      <c.icon className="w-4 h-4 text-[var(--w-text-dim)]" />
                    </span>
                  )}
                  <span className={`text-[16px] font-bold ${c.highlight ? "text-black" : "text-[var(--w-text)]"}`}>{c.title}</span>
                  {c.highlight && <Pill tone="brand" className="ml-auto">You</Pill>}
                </div>
                <ul className="space-y-2.5">
                  {c.rows.map((r) => (
                    <li key={r.t} className="flex items-start gap-2.5">
                      {r.ok ? (
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${c.highlight ? "text-black" : "text-[var(--w-green)]"}`} />
                      ) : (
                        <X className="w-4 h-4 mt-0.5 shrink-0 text-[var(--w-text-mute)]" />
                      )}
                      <span className={`text-[13.5px] leading-5 ${c.highlight ? "text-black/80" : r.ok ? "text-[var(--w-text-dim)]" : "text-[var(--w-text-mute)]"}`}>
                        {r.t}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
